import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';
import { Grid } from '@mui/material';
import { MarketButton } from '@market/react';
import { useOrderDetails } from 'components/context/order_details';
import { showPaymentForm } from 'utilities/cart';
import { buildLegalLink } from 'utilities/links';
import RecaptchaWorkaround from 'typescript/apps/checkout_v3/components/recaptcha/recaptcha_workaround';
import { useRequestRecaptchaFallback } from 'typescript/hooks/use_order_actions_client';

const SubmitWithRecaptcha = ({
  className,
  disabled = false,
  onClick = null,
  orderData,
  setRecaptchaResponse,
  submit,
}) => {
  const { t } = useTranslation();
  const {
    language,
    recaptchaKey,
    number: orderNumber,
    countryCode,
    languageCode,
  } = useOrderDetails();

  const { requestRecaptchaFallback } = useRequestRecaptchaFallback({
    orderNumber,
    countryCode,
    languageCode,
  });

  const [captchaLoaded, setCaptchaLoaded] = useState(false);

  // if they did not see the payment form, make them hit a recaptcha. but why the second clause?
  // tax free US installment orders still see the payment form for card on file but pay nothing upfront.
  const showRecaptcha =
    recaptchaKey &&
    (!showPaymentForm(orderData) ||
      Number.parseFloat(orderData.payment_due.amount) === 0);

  const isWarranty = orderData.is_warranty;

  return (
    <Grid
      container
      item
      className={className}
      spacing={2}
      justifyContent="flex-end"
    >
      {showRecaptcha && (
        <Grid item xs={12} md={6}>
          <RecaptchaWorkaround
            hl={language}
            sitekey={recaptchaKey}
            onVerify={setRecaptchaResponse}
            onLoad={() => setCaptchaLoaded(true)}
            onError={async (recaptchaError) => {
              captureException(recaptchaError, {
                tags: {
                  orderNumber,
                },
              });

              // we'll still set it to true to not block checkout if the recaptcha fails to load
              setCaptchaLoaded(true);

              try {
                const fallbackToken = await requestRecaptchaFallback();
                setRecaptchaResponse(fallbackToken);
              } catch (fallbackError) {
                // if fallback challenge fails, capture error and disable recaptcha
                captureException(fallbackError, {
                  tags: {
                    orderNumber,
                  },
                });

                // Set recaptcha response to null to indicate failure
                setRecaptchaResponse(null);
              }
            }}
            onExpire={() => setRecaptchaResponse(null)}
          />
        </Grid>
      )}
      <Grid item xs={12} md={isWarranty ? 12 : 6}>
        <MarketButton
          id="complete-your-order-btn"
          disabled={disabled || (showRecaptcha && !captchaLoaded)}
          rank="primary"
          onClick={onClick ?? submit}
          style={{ width: '100%' }}
        >
          {isWarranty
            ? t('checkout.label.submitOrder')
            : t('checkout.label.completeYourOrder')}
        </MarketButton>
      </Grid>
      {isWarranty && (
        <Grid item xs={12} container justifyContent="center">
          <p className="paragraph-20">
            <Trans i18nKey="checkout.label.viewPolicy">
              <a
                href={buildLegalLink('/hardware')}
                target="_blank"
                rel="noreferrer"
              />
            </Trans>
          </p>
        </Grid>
      )}
    </Grid>
  );
};

export default SubmitWithRecaptcha;
